import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { AppService } from "src/app/_services/app.service";
import { TranslateService } from "@ngx-translate/core";

interface ActivityFeedItem {
  initials: string;
  color: string;
  title: string;
  occurredAt?: string;
  actionKey?: string;
  metaKey?: string;
  actionText?: string;
  metaText?: string;
}

@Component({
  selector: "app-activity-feed-section",
  templateUrl: "./activity-feed-section.component.html",
  styleUrls: ["./activity-feed-section.component.scss"],
})
export class ActivityFeedSectionComponent {
  private readonly destroy$ = new Subject<void>();
  private refreshTimer?: ReturnType<typeof setInterval>;
  readonly titleText: string;
  readonly headTitleText: string;
  readonly badgeText: string;
  readonly skeletonItems = [1, 2, 3, 4];
  isLoading = true;
  feedItems: ActivityFeedItem[] = [];

  constructor(private appService: AppService, private translate: TranslateService) {
    this.titleText = this.translate.instant("UX_TEXT.ACTIVITY_FEED.TITLE");
    this.headTitleText = this.translate.instant("UX_TEXT.ACTIVITY_FEED.HEAD_TITLE");
    this.badgeText = this.translate.instant("UX_TEXT.ACTIVITY_FEED.BADGE");
  }

  ngOnInit(): void {
    this.loadLiveActivity();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get loopingItems() {
    return [...this.feedItems, ...this.feedItems];
  }

  get scrollDurationSeconds(): number {
    // Keep perceived speed stable when feed length changes.
    return Math.max(12, this.feedItems.length * 2.4);
  }

  private loadLiveActivity() {
    this.appService
      .getLiveActivity()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const mapped = this.mapLiveActivity(response);
          this.feedItems = mapped;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private startAutoRefresh() {
    this.refreshTimer = setInterval(() => {
      this.loadLiveActivity();
    }, 2.5 * 60 * 1000);
  }

  private mapLiveActivity(response: any): ActivityFeedItem[] {
    const rawItems = response?.items || response?.activities || response?.live_activities || response?.data || [];

    if (!Array.isArray(rawItems)) return [];

    return [...rawItems]
      .sort((a: any, b: any) => this.getTimestampMs(a) - this.getTimestampMs(b))
      .map((item: any) => {
        const title =
          (item?.actor_display_name ||
            item?.title ||
            item?.name ||
            item?.user_name ||
            item?.client_name ||
            item?.provider_name ||
            "")
            .toString()
            .trim();

        const actionText = this.formatActionLabel(
          (item?.action || item?.activity || "").toString().trim(),
          (item?.domaine_label || item?.domain || "").toString().trim(),
          (item?.description || item?.message || "").toString().trim()
        );

        const occurredAt = (item?.occurred_at || item?.created_at || "").toString().trim();
        const timeText =
          (item?.time || item?.time_text || item?.created_at_human || "").toString().trim() ||
          this.formatElapsed(occurredAt);
        const cityText = (item?.city_label || item?.city || item?.ville || item?.location || "").toString().trim();
        const metaText = [timeText, cityText].filter(Boolean).join(" - ");

        if (!title && !actionText) return null;

        return {
          initials: this.getInitials(title || actionText),
          color: this.getColorFromSeed(title || actionText),
          title: title || "BOB",
          occurredAt,
          actionText,
          metaText,
        } as ActivityFeedItem;
      })
      .filter((item): item is ActivityFeedItem => !!item);
  }

  private getTimestampMs(item: any): number {
    const raw = item?.occurred_at || item?.created_at || item?.time || "";
    const parsed = new Date(raw).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private formatActionLabel(action: string, domainLabel: string, fallbackText: string): string {
    const domain = domainLabel ? ` - ${domainLabel}` : "";
    switch (action) {
      case "demande_created":
        return `a cree une demande${domain}`;
      case "demande_accepted":
        return `a accepte${domain}`;
      case "demande_canceled":
        return `a annule${domain}`;
      case "demande_finished":
        return `a termine${domain}`;
      case "meet_delayed":
        return `rendez-vous retarde${domain}`;
      default:
        if (fallbackText) return fallbackText;
        if (!action) return "";
        return action.replace(/_/g, " ");
    }
  }

  private formatElapsed(isoDate: string): string {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return "";

    const diffMs = Date.now() - date.getTime();
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    if (minutes < 60) return `il y a ${minutes} min`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;

    const days = Math.floor(hours / 24);
    return `il y a ${days} j`;
  }

  private getInitials(value: string): string {
    const chunks = value
      .split(" ")
      .map((part) => part.trim())
      .filter(Boolean);
    if (!chunks.length) return "BE";
    if (chunks.length === 1) return chunks[0].slice(0, 2).toUpperCase();
    return `${chunks[0][0] ?? ""}${chunks[1][0] ?? ""}`.toUpperCase();
  }

  private getColorFromSeed(seed: string): string {
    const palette = ["#10b981", "#ec4899", "#60a5fa", "#a78bfa", "#f59e0b", "#14b8a6"];
    const hash = seed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return palette[hash % palette.length];
  }

  getActionText(item: ActivityFeedItem): string {
    return item.actionText || "";
  }

  getMetaText(item: ActivityFeedItem): string {
    return item.metaText || "";
  }
}
